import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { manualSync } from 'loot-core/client/actions';
import { format as monthUtilFormat } from 'loot-core/src/shared/months';

import { useAuth } from '../../../hooks/useAuth';
import { useDateFormat } from '../../../hooks/useDateFormat';
import { useNavigate } from '../../../hooks/useNavigate';
import { useSyncServerStatus } from '../../../hooks/useSyncServerStatus';
import { styles, theme } from '../../../style';
import { AnimatedRefresh } from '../../AnimatedRefresh';
import AccountStatusIndicator from '../../common/AccountStatusIndicator';
import { Button } from '../../common/Button';
import { Card } from '../../common/Card';
import { Text } from '../../common/Text';
import { Field, Row, Table, TableHeader } from '../../table';

type IDashboardAccountsProps = {
  accounts: IDashboardAccount[];
};

export type IDashboardAccount = {
  id: string;
  name: string;
  status: string;
  isLocal: boolean;
  syncTimestamp: string;
  isClosed: boolean;
};

const ROW_HEIGHT = 43;

const DashboardAccounts: React.FC<IDashboardAccountsProps> = ({ accounts }) => {
  const dispatch = useDispatch();
  const dateFormat = useDateFormat() || 'MM/dd/yyyy';
  const syncServerStatus = useSyncServerStatus();
  const navigate = useNavigate();
  const isServerOffline = syncServerStatus === 'offline';
  const isUsingServer = syncServerStatus !== 'no-server';
  const accountsSyncing = useSelector(state => state.account.accountsSyncing);
  const { billingStatus } = useAuth();

  const [isExpanded, setIsExpanded] = useState(false);

  const onSync = async (account: IDashboardAccount) => {
    await dispatch(manualSync(account.id));
  };

  const onSelectAccount = id => {
    navigate(`/accounts/${id}`);
  };

  function renderAccount({ item: account }: { item: IDashboardAccount }) {
    const canSync = account && account.id && !account.isClosed && isUsingServer;
    return (
      <Row
        height={ROW_HEIGHT}
        inset={0}
        style={{
          backgroundColor: theme.tableBackground,
          color: theme.tableText,
          ':hover': { backgroundColor: theme.tableRowBackgroundHover },
          cursor: 'pointer',
        }}
        onClick={() => onSelectAccount(account.id)}
      >
        <Field width="flex" name="name">
          <Text>{account.name}</Text>
        </Field>
        <Field width={70} name="status">
          <Text>
            <AccountStatusIndicator
              status={account.status}
              accountId={account.id}
            />
          </Text>
        </Field>
        <Field width={80} name="isLinked">
          <Text>{account.isLocal ? 'Local' : 'Linked'}</Text>
        </Field>
        <Field width={90} name="sync_timestamp">
          <Text>
            {account.syncTimestamp
              ? monthUtilFormat(account.syncTimestamp, dateFormat)
              : 'N/A'}
          </Text>
        </Field>
        <Field width={30} name="sync">
          {!account.isLocal ? (
            <Button
              data-adoption-id="account-import-btn"
              type="bare"
              onClick={() => onSync(account)}
              disabled={canSync && isServerOffline}
              style={{
                cursor: 'pointer',
                ...(!billingStatus?.isActive
                  ? { color: theme.tableTextLight }
                  : {}),
              }}
            >
              <AnimatedRefresh
                width={13}
                height={13}
                animating={
                  account
                    ? accountsSyncing.includes(account.id)
                    : accountsSyncing.length > 0
                }
              />
            </Button>
          ) : null}
        </Field>
      </Row>
    );
  }

  return (
    <Card className="card">
      <p style={{ ...styles.mediumText, fontWeight: 700 }}>Accounts</p>
      {accounts.length ? (
        <>
          <TableHeader height={ROW_HEIGHT}>
            <Field width="flex">Name</Field>
            <Field width={70} style={{ alignItems: 'center' }}>
              Status
            </Field>
            <Field width={80}>Type</Field>
            <Field width={90}>Sync date</Field>
            <Field width={30} />
          </TableHeader>
          <Table
            rowHeight={ROW_HEIGHT}
            backgroundColor="transparent"
            style={{ flex: 1, backgroundColor: 'transparent' }}
            items={
              (isExpanded
                ? accounts
                : accounts.slice(0, 10)) as IDashboardAccount[]
            }
            renderItem={renderAccount}
            allowPopupsEscape={accounts.length < 6}
          />
          {accounts?.length > 10 ? (
            <p
              className="accounts-view-more"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </p>
          ) : null}
        </>
      ) : (
        <div className="placeholder-container">
          You don't have any accounts yet
        </div>
      )}
    </Card>
  );
};
export default DashboardAccounts;
